import { useMemo } from "react";
import { useAuthStore } from "@/store/auth";
import { useAccess } from "@/hooks/useAccess";
import AccessHelper, { type UserRoute } from "@/lib/utils/AccessHelper";

function normalizePath(path: string | undefined): string {
  const p = (path || "").replace(/\/$/, "") || "/";
  return p.startsWith("/") ? p : `/${p}`;
}

function pathMatchesCandidate(path: string, candidate: string): boolean {
  const normalizedPath = normalizePath(path);
  const normalizedCandidate = normalizePath(candidate);
  return (
    normalizedPath === normalizedCandidate ||
    normalizedPath.endsWith(normalizedCandidate)
  );
}

/**
 * Role permissions for shipping-line financial flows, keyed by navigation rows
 * under Shipping Lines (matches NavigationSeeder `name` values).
 * Uses the same permission matrix as AccessHelper.hasAccess without pathname resolution.
 */
export function getShippingFinancialStatementPermissionFlags(
  access: InstanceType<typeof AccessHelper>,
  userRoutes: UserRoute[] | undefined,
) {
  const list = userRoutes ?? [];
  const shipping = list.find((r) => normalizePath(r.path) === "/shipping-lines");
  const shippingNavId = shipping?.id;
  const children = shipping?.children ?? [];

  const byName = (name: string) => children.find((c) => c.name === name);
  const byAnyName = (names: string[]) =>
    names.map((n) => byName(n)).find((v) => !!v);
  const byPathCandidates = (candidates: string[]) =>
    children.find((c) =>
      candidates.some((candidate) =>
        pathMatchesCandidate(c.path, candidate),
      ),
    );
  const byPathThenName = (paths: string[], names: string[]) =>
    byPathCandidates(paths) ?? byAnyName(names);

  const perm = (nav?: UserRoute) => {
    if (shippingNavId == null || nav?.id == null) return undefined;
    return access.getNavigationPermission(shippingNavId, nav?.id);
  };

  const financialRoot = byPathThenName(
    ["/financial-statements/:shippingLineId"],
    ["Financial Statements"],
  );
  const soa = byPathThenName(
    ["/financial-statements/:shippingLineId/soa"],
    ["Statement of Account", "SOA"],
  );
  const billing = byPathThenName(
    ["/financial-statements/:shippingLineId/billing"],
    ["Billing Statement", "Billing Statements"],
  );
  const invoice = byPathThenName(
    ["/financial-statements/:shippingLineId/invoice"],
    ["Invoice", "Invoices"],
  );
  const soaDownload = byPathThenName(
    ["/financial-statements/:shippingLineId/soa/:soaId/download"],
    ["Download SOA"],
  );
  const soaEmail = byPathThenName(
    ["/financial-statements/:shippingLineId/soa/:soaId/email"],
    ["Email SOA to Client", "Send SOA to Client"],
  );
  const billingDownload = byPathThenName(
    ["/financial-statements/:shippingLineId/billing/:billingId/download"],
    ["Download Billing Statement"],
  );
  const billingDownloadSoa = byPathThenName(
    [
      "/financial-statements/:shippingLineId/billing/:billingId/download-soa-and-billing",
    ],
    ["Download Billing Statement and SOA"],
  );
  const billingEmail = byPathThenName(
    ["/financial-statements/:shippingLineId/billing/:billingId/email"],
    ["Email Billing Statement to Client", "Send Billing Statement to Client"],
  );
  const billingEmailSoa = byPathThenName(
    ["/financial-statements/:shippingLineId/billing/:billingId/email-soa-and-billing"],
    [
      "Email Billing Statement and SOA to Client",
      "Send Billing Statement and SOA to Client",
    ],
  );
  const invoiceDownload = byPathThenName(
    ["/financial-statements/:shippingLineId/invoice/:invoiceId/download"],
    ["Download Invoice"],
  );
  const invoiceEmail = byPathThenName(
    ["/financial-statements/:shippingLineId/invoice/:invoiceId/email"],
    ["Email Invoice to Client", "Send Invoice to Client"],
  );

  return {
    shippingNavId,
    canViewFinancialStatementsPage: !!(perm(financialRoot)?.can_view),
    soaView: !!(perm(soa)?.can_view),
    soaCreate: !!(perm(soa)?.can_create),
    soaEdit: !!(perm(soa)?.can_edit),
    soaDownload: !!(perm(soaDownload)?.can_view),
    soaEmail: !!(perm(soaEmail)?.can_view),
    billingView: !!(perm(billing)?.can_view),
    billingCreate: !!(perm(billing)?.can_create),
    billingEdit: !!(perm(billing)?.can_edit),
    billingDownload: !!(perm(billingDownload)?.can_view),
    billingDownloadSoa: !!(perm(billingDownloadSoa)?.can_view),
    billingEmail: !!(perm(billingEmail)?.can_view),
    billingEmailSoa: !!(perm(billingEmailSoa)?.can_view),
    invoiceView: !!(perm(invoice)?.can_view),
    invoiceCreate: !!(perm(invoice)?.can_create),
    invoiceEdit: !!(perm(invoice)?.can_edit),
    invoiceDownload: !!(perm(invoiceDownload)?.can_view),
    invoiceEmail: !!(perm(invoiceEmail)?.can_view),
  };
}

export type ShippingFinancialStatementPermissionFlags = ReturnType<
  typeof getShippingFinancialStatementPermissionFlags
>;

export function useShippingFinancialStatementPermissionFlags(): ShippingFinancialStatementPermissionFlags {
  const userRoutes = useAuthStore((s) => s.user?.user_routes);
  const accessHelper = useAccess();
  return useMemo(
    () =>
      getShippingFinancialStatementPermissionFlags(
        accessHelper,
        userRoutes,
      ),
    [accessHelper, userRoutes],
  );
}
